import { Component, inject, OnDestroy, OnInit, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AnnouncerService } from '../../../core/services/announcer.service';
import { PwaService, SyncStatus } from '../../../core/services/pwa.service';
import { GlassCardComponent } from '../glass-card/glass-card.component';

@Component({
  selector: 'app-sync-status',
  standalone: true,
  imports: [CommonModule, GlassCardComponent],
  template: `
    @if (syncStatus().pending > 0 || syncStatus().isSyncing) {
      <app-glass-card tint="secondary" padding="sm" class="sync-status" role="status" aria-live="polite">
        <div class="sync-status__content">
          @if (syncStatus().isSyncing) {
            <div class="sync-status__spinner" aria-hidden="true"></div>
          }
          <span class="sync-status__text">
            @if (syncStatus().isSyncing) {
              همگام‌سازی در حال انجام...
            } @else {
              {{ syncStatus().pending }} مورد در انتظار همگام‌سازی
            }
          </span>
          @if (syncStatus().lastSync) {
            <span class="sync-status__last-sync">
              آخرین همگام‌سازی: {{ syncStatus().lastSync | date:'HH:mm:ss' }}
            </span>
          }
        </div>
      </app-glass-card>
    }
  `,
  styles: [`
    .sync-status {
      position: fixed;
      bottom: calc(var(--space-xl) + env(safe-area-inset-bottom) + 70px);
      left: var(--space-md);
      right: var(--space-md);
      max-width: 480px;
      margin-inline: auto;
      z-index: 999;
      animation: slide-up 0.3s var(--ease-out-spring) both;
    }

    @keyframes slide-up {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .sync-status__content {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
    }

    .sync-status__spinner {
      width: 16px;
      height: 16px;
      border: 2px solid var(--glass-border-soft);
      border-top-color: var(--color-secondary);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .sync-status__text {
      font-size: 0.78rem;
      color: var(--text-on-glass-strong);
      @include mixins.text-micro;
      font-weight: 500;
    }

    .sync-status__last-sync {
      font-size: 0.68rem;
      color: var(--text-on-glass-weak);
      @include mixins.text-micro;
      margin-inline-start: auto;
    }
  `],
})
export class SyncStatusComponent implements OnInit, OnDestroy {
  private readonly pwaService = inject(PwaService);
  private readonly announcer = inject(AnnouncerService);

  syncStatus = signal<SyncStatus>({
    pending: 0,
    lastSync: null,
    isSyncing: false,
  });

  ngOnInit(): void {
    this.pwaService.syncStatus$.subscribe((status) => this.syncStatus.set(status));
  }

  private readonly announceSync = effect(() => {
    const status = this.syncStatus();
    if (status.isSyncing) {
      this.announcer.announcePolite('همگام‌سازی در حال انجام است');
    } else if (status.pending === 0 && status.lastSync) {
      this.announcer.announcePolite('همگام‌سازی کامل شد');
    } else if (status.pending > 0 && !status.isSyncing) {
      this.announcer.announcePolite(`${status.pending} مورد در انتظار همگام‌سازی`);
    }
  });

  ngOnDestroy(): void {}
}